/**
 * Декодирование доменов вида xn--80aswg.xn--p1ai обратно в «сайт.рф».
 *
 * Браузер отдаёт hostname всегда в кодировке punycode (RFC 3492), а встроенного
 * декодера в JavaScript нет: модуль punycode из Node в сборку не попадает.
 * Здесь минимальная реализация обратного преобразования — ровно столько, сколько
 * нужно, чтобы показать русское имя сайта вместо «xn----…».
 */

const BASE = 36;
const T_MIN = 1;
const T_MAX = 26;
const SKEW = 38;
const DAMP = 700;
const INITIAL_BIAS = 72;
const INITIAL_N = 128;
const DELIMITER = '-';
const PREFIX = 'xn--';

function digitValue(codePoint) {
  // '0'-'9' → 26..35, 'a'-'z' и 'A'-'Z' → 0..25
  if (codePoint >= 0x30 && codePoint <= 0x39) return codePoint - 0x30 + 26;
  if (codePoint >= 0x61 && codePoint <= 0x7a) return codePoint - 0x61;
  if (codePoint >= 0x41 && codePoint <= 0x5a) return codePoint - 0x41;
  return BASE;
}

function adaptBias(delta, numPoints, firstTime) {
  let d = firstTime ? Math.floor(delta / DAMP) : delta >> 1;
  d += Math.floor(d / numPoints);
  let k = 0;
  while (d > ((BASE - T_MIN) * T_MAX) >> 1) {
    d = Math.floor(d / (BASE - T_MIN));
    k += BASE;
  }
  return k + Math.floor(((BASE - T_MIN + 1) * d) / (d + SKEW));
}

/** Одна метка домена без приставки xn--; при поломке возвращает null. */
function decodeLabel(input) {
  const output = [];
  let n = INITIAL_N;
  let i = 0;
  let bias = INITIAL_BIAS;

  const lastDelimiter = input.lastIndexOf(DELIMITER);
  if (lastDelimiter > 0) {
    for (let j = 0; j < lastDelimiter; j += 1) {
      const code = input.charCodeAt(j);
      if (code >= 0x80) return null;
      output.push(code);
    }
  }

  let index = lastDelimiter > 0 ? lastDelimiter + 1 : 0;
  while (index < input.length) {
    const oldI = i;
    let w = 1;
    for (let k = BASE; ; k += BASE) {
      if (index >= input.length) return null;
      const digit = digitValue(input.charCodeAt(index));
      index += 1;
      if (digit >= BASE) return null;
      i += digit * w;
      const t = k <= bias ? T_MIN : Math.min(Math.max(k - bias, T_MIN), T_MAX);
      if (digit < t) break;
      w *= BASE - t;
      if (!Number.isFinite(i) || !Number.isFinite(w)) return null;
    }
    bias = adaptBias(i - oldI, output.length + 1, oldI === 0);
    n += Math.floor(i / (output.length + 1));
    i %= output.length + 1;
    output.splice(i, 0, n);
    i += 1;
  }

  try {
    return String.fromCodePoint(...output);
  } catch (e) {
    return null;
  }
}

/**
 * Доменное имя в человеческом виде. Метки без приставки xn-- остаются как есть,
 * а при любой ошибке разбора возвращается исходная строка: показать «xn----…»
 * лучше, чем пустое место.
 */
export function decodeHostname(hostname) {
  if (!hostname || hostname.indexOf(PREFIX) === -1) return hostname || '';
  return hostname
    .split('.')
    .map((label) => {
      if (label.toLowerCase().indexOf(PREFIX) !== 0) return label;
      const decoded = decodeLabel(label.slice(PREFIX.length));
      // Если в результате нет ни одного нелатинского символа, значит метка была
      // не настоящей: показываем как есть, чтобы ничего не исказить.
      const hasNonAscii = Array.from(decoded || '').some((ch) => ch.codePointAt(0) > 0x7f);
      if (!decoded || !hasNonAscii) return label;
      return decoded;
    })
    .join('.');
}

export default decodeHostname;
